/**
 * DisplayPool — a small keyed pool for display objects that are created and
 * thrown away many times a second (ability primitives, projectile bodies).
 *
 * Why (plan 16): Pixi v8 rebuilds the whole stage's instruction set on any
 * frame where a child is added or removed, or a `visible`/`zIndex`/parent
 * flips. An ability cast created ~a dozen Graphics and destroyed them a few
 * hundred ms later, so a fight rebuilt the stage on ~22 % of frames. A pooled
 * object stays attached to its layer at its depth and is PARKED at alpha 0
 * (the PixiParticleEmitter precedent): reusing it is a transform/alpha write,
 * which Pixi treats as a cheap update, not a rebuild.
 *
 * Rules:
 *   - `release` parks the object (its own `park()`: alpha 0, transform reset).
 *     Parking never writes `visible`, `zIndex` or `parent`.
 *   - A key names everything that is structural or drawn: layer, depth and
 *     the draw signature. Re-acquiring a key therefore needs no redraw.
 *   - Per-key and global caps bound what parked objects cost (they stay in the
 *     scene graph). Overflow destroys the object and counts it — never silent.
 *
 * Substrate-agnostic: anything with park/unpark/destroy can be pooled.
 */

export interface Parkable {
  /** Hide by alpha 0 and reset transform. Must not write visible/zIndex/parent. */
  park(): void;
  /** Make the object usable again (alpha 1, live). The caller sets its transform. */
  unpark(): void;
  destroy(): void;
}

export function isParkable(obj: unknown): obj is Parkable {
  const o = obj as Partial<Parkable> | null;
  return !!o && typeof o.park === 'function' && typeof o.unpark === 'function';
}

export interface DisplayPoolOptions {
  /** Parked objects kept per key (default 6). */
  perKeyCap?: number;
  /** Parked objects kept over all keys (default 160). */
  globalCap?: number;
}

export interface DisplayPoolStats {
  live: number;
  parked: number;
  made: number;
  reused: number;
  /** Objects destroyed because a cap was full (plus drains). */
  destroyed: number;
  keys: number;
}

export class DisplayPool<T extends Parkable> {
  private readonly free = new Map<string, T[]>();
  private readonly perKeyCap: number;
  private readonly globalCap: number;
  private parkedCount = 0;
  private liveCount = 0;
  private made = 0;
  private reused = 0;
  private destroyed = 0;

  constructor(opts: DisplayPoolOptions = {}) {
    this.perKeyCap = opts.perKeyCap ?? 6;
    this.globalCap = opts.globalCap ?? 160;
  }

  /** A parked object for `key`, unparked; null when none is parked. */
  take(key: string): T | null {
    const obj = this.free.get(key)?.pop();
    if (!obj) return null;
    this.parkedCount--;
    this.reused++;
    this.liveCount++;
    obj.unpark();
    return obj;
  }

  /** Count an object the caller made itself after a `take` miss. */
  noteMade(): void {
    this.made++;
    this.liveCount++;
  }

  /** A parked object for `key`, or a new one from `make`. */
  acquire(key: string, make: () => T): T {
    const obj = this.take(key);
    if (obj) return obj;
    this.noteMade();
    return make();
  }

  /** Park `obj` under `key`, or destroy it when a cap is full. */
  release(key: string, obj: T): void {
    this.liveCount = Math.max(0, this.liveCount - 1);
    let list = this.free.get(key);
    if (!list) {
      list = [];
      this.free.set(key, list);
    }
    if (list.length >= this.perKeyCap || this.parkedCount >= this.globalCap) {
      this.destroyed++;
      obj.destroy();
      return;
    }
    obj.park();
    list.push(obj);
    this.parkedCount++;
  }

  /** Destroy every parked object (map switch / shutdown). Returns how many. */
  drain(): number {
    let n = 0;
    for (const list of this.free.values()) {
      for (const obj of list) {
        obj.destroy();
        n++;
      }
    }
    this.free.clear();
    this.parkedCount = 0;
    this.destroyed += n;
    return n;
  }

  stats(): DisplayPoolStats {
    return {
      live: this.liveCount,
      parked: this.parkedCount,
      made: this.made,
      reused: this.reused,
      destroyed: this.destroyed,
      keys: this.free.size,
    };
  }
}
